import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  MenuOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, message, Tooltip, Typography } from 'antd';
import JSZip from 'jszip';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isLayoutBlockType,
  type WorkflowField,
  type WorkflowStep,
  type WorkflowTemplate,
} from '@/api/workflows';
import { notifySessionActivity } from '@/auth/sessionTimer';
import { type MeResponse, useSession } from '@/auth/useSession';

import { AdditionalDocumentsStep } from './AdditionalDocumentsStep';
import { requiredExemptionFor, resolveAdvisorSourceValue } from './advisorPrefill';
import { ApplicationReviewStep } from './ApplicationReviewStep';
import { evaluateConditions } from './evaluateConditions';
import { type FieldValue, type RepeatableRowValue, isBlockingValueMatch } from './FieldRenderer';
import { meetsMinSelections, validateMinAge } from './fieldValidation';
import { FormRenderer } from './FormRenderer';
import { hiddenBorrowedFieldIds } from './hiddenReveals';
import { OneSpanESignStep } from './OneSpanESignStep';
import { type PdfCache, PdfGenerationStep } from './PdfGenerationStep';
import { buildFilledPdfBlobUrl, collectPdfDocs, triggerBlobDownload } from './pdfPreviewActions';
import { PlaceholderStep } from './PlaceholderStep';
import { isSumSatisfied, unbalancedUnitColumns } from './repeatableSum';
import {
  getInstanceCount,
  renderInstanceLabel,
  scopeValuesToInstance,
  sectionMinInstances,
} from './sectionInstances';
import { findAllUniquenessConflicts, findUniquenessConflict } from './uniqueness';
import type { UploadedFilesStore } from './uploadedFiles';
import { flattenUploadedFiles } from './uploadedFiles';
import { WelcomeStep } from './WelcomeStep';

const { Text } = Typography;

type Props = {
  template: WorkflowTemplate;
  language: 'en' | 'fr';
  mode: 'preview' | 'advisor';
  /** Step to land on when the shell mounts. Falls back to the first eligible
   *  step (and a saved-draft step if a localStorage draft exists). Used by
   *  the Approver review path so clicking "Preview" on a specific step rail
   *  card opens the form on that step. */
  initialStepId?: string;
  /** Seed values for testing — Originator/Approver Preview can pass a fixture
   *  loaded from `public/sample-values.json` so the form lands pre-filled and
   *  the Application Review step has data to recap. Ignored in advisor mode
   *  (advisor draft is the source of truth there). */
  initialValues?: Record<string, FieldValue>;
  /** Fired when the advisor clicks "Save and exit" — caller decides where to
   *  go (typically back to the workflow detail page). Optional so existing
   *  call sites that don't need it can stay as-is. */
  onSaveAndExit?: () => void;
};

/**
 * Two-column shell shared by Preview + Advisor: left rail listing every
 * DynamicForm step (with a blue check when every required field on that step
 * is filled), main column rendering the currently-selected step. Owns the
 * values map across step navigation so partially-filled steps keep their
 * input across rail clicks.
 *
 * In `'preview'` mode all rail entries are clickable regardless of completion;
 * in `'advisor'` mode (later) we'll gate navigation on sequential progression.
 *
 * Non-DynamicForm steps are filtered out — they don't have a "form" to
 * render. The user said only DynamicForm in the rail for now.
 */
export function FormShell({
  template,
  language: initialLanguage,
  mode,
  initialStepId,
  initialValues,
  onSaveAndExit,
}: Props) {
  const { t, i18n } = useTranslation();
  // UI language — everything the advisor READS follows the app-level locale
  // (AppShell header switcher). Live-follows i18n.language so a header
  // toggle mid-form flips rail titles + field labels + chrome on the spot.
  // Previously a Welcome-type step's language radio flipped this too, which
  // meant the whole form re-rendered in the CLIENT's chosen language
  // instead of the advisor's — Lintao's fix.
  const language: 'en' | 'fr' = i18n.language.startsWith('fr') ? 'fr' : 'en';
  // PDF language — separate signal that ONLY drives which PDF template gets
  // pulled from S3 + filled at PdfGeneration (and the OneSpan e-sign
  // config's template pick, downstream). Set by the Welcome step's radio
  // when the workflow has one; otherwise it follows the advisor's UI locale
  // (see the sync effect below). Seeded from the mount-time `initialLanguage`
  // prop. Consumers that pick between `enFileKey` / `frFileKey` read this,
  // not `language`.
  const [pdfLanguage, setPdfLanguage] = useState<'en' | 'fr'>(initialLanguage);
  // Does this workflow have a client-facing Welcome step? When it does, that
  // step's radio is the sole authority on the PDF language and the sync effect
  // below stays out of the way. When it doesn't, there's no place for the
  // client to pick a PDF language, so it tracks the app-level locale instead.
  const hasWelcomeStep = useMemo(
    () => template.steps.some((s) => s.type === 'Welcome'),
    [template.steps],
  );
  // No-Welcome fallback: keep the PDF language in lockstep with the advisor's
  // UI locale. Without this, `pdfLanguage` froze at whatever the locale was on
  // mount (useState only reads its initial arg once) and a header language
  // switch mid-form re-languaged the UI but silently left the generated PDF in
  // the old language. Guarded on `hasWelcomeStep` so a workflow WITH a Welcome
  // radio still lets the client's choice diverge from the advisor's UI.
  useEffect(() => {
    if (hasWelcomeStep) return;
    setPdfLanguage(language);
  }, [hasWelcomeStep, language]);
  // Advisor-facing rail. Only PdfFileUpload is excluded — those are purely
  // originator-side source-template configuration the advisor never sees. The
  // advisor walks every other type in order:
  //   DynamicForm → ApplicationReview → OneSpanESign → AdditionalDocuments
  //   → PdfGeneration → Submit
  // (post-2026-06-17 flow redesign — Application Review + flipped order).
  const allSteps = useMemo(
    () => template.steps.filter((s) => s.type !== 'PdfFileUpload'),
    [template.steps],
  );

  // No browser storage. Values + nav state live in React memory only — the
  // BMO integration will own real persistence later (every ~20s autosave +
  // on Next + on Save and Exit, dispatching to BMO's backend). Until that
  // ships the advisor session is in-tab only; closing the tab loses work.
  // Documented tradeoff per user 2026-06-18.
  const seededStepId = (() => {
    if (initialStepId && allSteps.some((s) => s.id === initialStepId)) return initialStepId;
    return allSteps[0]?.id ?? null;
  })();
  const [activeStepId, setActiveStepId] = useState<string | null>(seededStepId);
  const [values, setValues] = useState<Record<string, FieldValue>>(initialValues ?? {});
  // Advisor-uploaded documents from any AdditionalDocuments step. Hoisted
  // here (rather than inside AdditionalDocumentsStep) so the state
  // survives step navigation AND so the downstream PdfGeneration step
  // can list them alongside the filled PDF templates, and the "Download
  // all" button can zip both into a single advisor deliverable. Keyed
  // by entryId for named mandatory/optional entries + a `bucket` array
  // for the free-form optional bucket.
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFilesStore>({
    entries: {},
    bucket: [],
  });
  // Track which fields the advisor has typed into — used to sever
  // `sourceField` prefill links. Once a field is in this set, the prefill
  // effect stops overwriting it even if the source field's value later
  // changes. Cleared implicitly when FormShell unmounts.
  const editedFieldsRef = useRef<Set<string>>(new Set());
  // Bumped whenever an advisor edit hits `editedFieldsRef`, so downstream
  // useMemos that depend on the edited set can revalidate (React can't see
  // a mutation on a ref).
  const [editedTick, setEditedTick] = useState(0);

  // Rail overlay open/close — the ONLY responsive-related JS state. Whether the
  // rail collapses at all is decided purely in CSS (`@container` on
  // `.form-shell`, see theme/global.css); this just tracks the slide-in toggle
  // when it's collapsed, surfaced to CSS via the `--rail-open` modifier class.
  const [railOpen, setRailOpen] = useState(false);

  // Step-level Next blocker set by the currently-rendered step content
  // (INFLOWS-188: OneSpanESignStep blocks Next when any signatory email
  // fails ZeroBounce verification). Value is the tooltip message to show
  // on the disabled button; null means the step isn't blocking anything.
  // Cleared automatically on step switch — the StepContent's key={id}
  // remount unmounts the OneSpan effect that sets it.
  const [stepBlocker, setStepBlocker] = useState<string | null>(null);

  // Session-scoped prefill for fields carrying `sourceValue`. Runs when the
  // /me session lands. Applies to EVERY field on EVERY step whose current
  // value is empty — advisor edits are preserved (a value only gets written
  // when the slot is unset). Rate-limited by the resolver + useSession's
  // module-level cache; the effect body itself only fires when the session
  // ref changes.
  const { user } = useSession();
  useEffect(() => {
    if (!user) return;
    const resolve = (key: string): string | null => resolveAdvisorSourceValue(key, user);
    setValues((prev) => {
      let next: Record<string, FieldValue> = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm') continue;
        for (const section of step.sections) {
          // §1 Repeatable Sections: prefill EVERY current instance's
          // qualified slot when the section is repeatable. Otherwise
          // fall through to the bare id (one iteration).
          const instanceCount = section.repeatable
            ? getInstanceCount(next, section.id, sectionMinInstances(section))
            : 1;
          for (const row of section.rows) {
            for (const col of row.columns) {
              const field = col.field;
              if (!field?.sourceValue) continue;
              const resolved = resolve(field.sourceValue);
              if (resolved === null) continue;
              for (let i = 0; i < instanceCount; i++) {
                const key = section.repeatable ? `inst:${section.id}:${i}:${field.id}` : field.id;
                const already = next[key];
                if (already !== undefined && already !== null && already !== '') continue;
                if (next === prev) next = { ...prev };
                next[key] = resolved;
              }
            }
          }
        }
      }
      return next;
    });
  }, [user, template]);

  // Cross-step field prefill (§2). On every values change, walk every
  // DynamicForm field carrying a `sourceField`: if the target is still
  // empty AND the advisor hasn't manually edited it AND the source has a
  // non-empty value, copy the source into the target. Advisor typing
  // severs the link via `editedFieldsRef`. The effect runs whenever the
  // source's value changes so subsequent typing on an unedited target
  // still pulls the latest source. Compared strictly by value + edit-set;
  // `editedTick` is the state-visible witness to ref mutations.
  useEffect(() => {
    // Build a lookup of each authored field's containing section id, so
    // we can resolve BOTH sides of a sourceField relationship to a
    // qualified key when either side lives inside a repeatable section.
    // The source resolves to instance 0 (the primary answer) — a
    // repeatable-source is a single-instance authoring pattern in
    // practice today; extending to per-instance sourcing would need a
    // per-target instance identity we don't yet plumb.
    const sectionByField = new Map<string, { id: string; repeatable: boolean }>();
    // Authored fields by id — used to resolve a prefill source back to the
    // field itself so we can compare uniqueness groups across the link.
    const sourceFieldById = new Map<string, WorkflowField>();
    for (const step of template.steps) {
      if (step.type !== 'DynamicForm') continue;
      for (const section of step.sections) {
        for (const row of section.rows) {
          for (const col of row.columns) {
            if (col.field) {
              sectionByField.set(col.field.id, {
                id: section.id,
                repeatable: section.repeatable === true,
              });
              sourceFieldById.set(col.field.id, col.field);
            }
          }
        }
      }
    }
    const keyFor = (fieldId: string, instanceIdx: number): string => {
      const sec = sectionByField.get(fieldId);
      if (!sec || !sec.repeatable) return fieldId;
      return `inst:${sec.id}:${instanceIdx}:${fieldId}`;
    };
    setValues((prev) => {
      let next: Record<string, FieldValue> = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm') continue;
        for (const section of step.sections) {
          const targetInstances = section.repeatable
            ? getInstanceCount(next, section.id, sectionMinInstances(section))
            : 1;
          for (const row of section.rows) {
            for (const col of row.columns) {
              const field = col.field;
              if (!field?.sourceField) continue;
              // A field can be authored with BOTH a prefill source and a
              // uniquenessGroup shared with that source — RDSP does exactly
              // this (Beneficiary Mobile Phone / Email prefill from Account
              // Holder, and both sides sit in signatoryPhone /
              // signatoryEmail). Those two rules contradict each other:
              // copying makes the values identical, then uniqueness rejects
              // them for being identical, and the advisor gets
              // "already used by 'Mobile Phone'" against a value they never
              // typed. The platform must never generate a value it will
              // immediately refuse.
              //
              // The uniqueness group is a hard CONSTRAINT; a prefill is a
              // soft DEFAULT. The default yields. Everything else in the
              // section still prefills — only fields that must differ from
              // their own source stay empty for the advisor to fill in.
              if (sharesUniquenessGroupWithSource(field, sourceFieldById)) continue;
              // Source lives under either its bare id or under its
              // section's instance-0 qualified key.
              const sourceKey = keyFor(field.sourceField.fieldId, 0);
              const sourceValue = next[sourceKey];
              // Skip when the source is blank — no meaningful value to
              // propagate yet, and we don't want to overwrite a sane
              // default with "".
              if (
                sourceValue === undefined ||
                sourceValue === null ||
                (typeof sourceValue === 'string' && sourceValue.trim().length === 0)
              ) {
                continue;
              }
              for (let i = 0; i < targetInstances; i++) {
                const targetKey = section.repeatable
                  ? `inst:${section.id}:${i}:${field.id}`
                  : field.id;
                // Advisor typing into THIS target severs the prefill link.
                // editedFieldsRef records the SAME key shape onChange emits
                // (the qualified instance key for a repeatable field, the
                // bare id otherwise), so match on targetKey — a bare-id
                // check would miss per-instance edits and clobber them.
                if (editedFieldsRef.current.has(targetKey)) continue;
                const current = next[targetKey];
                // Already mirrors the source — no-op to avoid churn and let
                // the effect settle without another values change.
                if (current === sourceValue) continue;
                // Keep the unedited target in sync with the LATEST source
                // value, overwriting any earlier partial copy. The previous
                // "only write while the target is empty" guard froze the
                // target at whatever the source held on the first effect run
                // — typically the first word the advisor had typed — and
                // never caught up as they finished the rest of the value.
                if (next === prev) next = { ...prev };
                next[targetKey] = sourceValue;
              }
            }
          }
        }
      }
      return next;
    });
    // editedTick is intentionally in the dep set — mutations to the ref
    // itself don't re-run the effect, so we bump the tick when an edit
    // is recorded to let this effect re-observe the frozen set.
  }, [template, values, editedTick]);
  // Filled-PDF blob URL cache — survives step navigation because it lives on
  // FormShell (which stays mounted across step changes). Keyed by
  // StepFileEntry.id; each entry stores the `values` reference the URL was
  // built from so PdfGenerationStep can detect a stale cache when the
  // advisor edits an answer and revisits the final step. FormShell's own
  // unmount revokes everything.
  const pdfCacheRef = useRef<Map<string, { values: Record<string, FieldValue>; url: string }>>(
    new Map(),
  );
  useEffect(
    () => () => {
      pdfCacheRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
      pdfCacheRef.current.clear();
    },
    [],
  );
  // Steps the advisor has Next'd OFF OF. A step only paints the rail's
  // blue check after they've explicitly advanced past it (not the moment
  // the renderer thinks it's vacuously "complete" — e.g. a step with no
  // required fields would otherwise show the check the moment it lands
  // in the workflow, which surprised an originator reviewing the BMO
  // Carlyle smartform's Accredited Investor step).
  const [advancedSteps, setAdvancedSteps] = useState<Set<string>>(() => new Set());

  // Validate that `activeStepId` still points at a step that exists AND is
  // currently visible. If the visibility flipped (or the saved id is stale),
  // jump state forward to the first visible step in document order so the
  // persisted draft, rail highlight, and nav arithmetic all agree. Without
  // this, state pins to a hidden step and a future re-show snaps the user
  // back unexpectedly.
  useEffect(() => {
    if (allSteps.length === 0) return;
    // Preview mode lets a reviewer LAND on (and stay on) a conditionally-hidden
    // step reached via the per-step "Preview" deep link (?stepId=…), so they can
    // inspect it without walking the whole flow. There we only require the step
    // to EXIST — a hidden-but-real step stays active. Advisor mode still requires
    // visibility, so the applicant is jumped off any step conditions have hidden.
    // A genuinely stale id (matches no step) falls through to the first-visible
    // jump in BOTH modes. Note this only governs the active step; Back/Next still
    // skip hidden steps in every mode (see prevStep/nextStep below).
    const stillValid =
      activeStepId !== null &&
      allSteps.some(
        (s) =>
          s.id === activeStepId &&
          (mode === 'preview' || evaluateConditions(s.conditions, s.conditionCombinator, values)),
      );
    if (stillValid) return;
    const firstVisible =
      allSteps.find((s) => evaluateConditions(s.conditions, s.conditionCombinator, values))?.id ??
      allSteps[0]?.id ??
      null;
    if (firstVisible !== activeStepId) setActiveStepId(firstVisible);
  }, [allSteps, values, activeStepId, mode]);

  // Memoize the per-step visibility map ONCE per `values` change. A pure
  // function of `(allSteps, values)`, so re-evaluating on every render (the
  // pre-memo behaviour) re-walked the entire workflow on every keystroke
  // — measurable lag on 40+ field forms. The map is cheap to lookup and
  // every downstream computation (visibleSteps / activeStep / prev / next
  // / rail-per-step-isComplete) can share it.
  const stepVisibility = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of allSteps) {
      map.set(s.id, evaluateConditions(s.conditions, s.conditionCombinator, values));
    }
    return map;
  }, [allSteps, values]);
  const isVisible = useCallback(
    (s: WorkflowStep) => stepVisibility.get(s.id) ?? true,
    [stepVisibility],
  );
  const visibleSteps = useMemo(() => allSteps.filter(isVisible), [allSteps, isVisible]);
  // Steps whose conditions are not met — shown in the rail but inert.
  // Declared here, alongside the other hooks and before any early return, so
  // hook order is stable across renders. Reactive to `values`, so rows
  // enable/disable live as conditions flip.
  const railDisabled = useMemo(
    () => new Set(allSteps.filter((s) => !isVisible(s)).map((s) => s.id)),
    [allSteps, isVisible],
  );
  // Advisor-only: steps the applicant has not reached yet. `advancedSteps`
  // only gains a step when Next is pressed, and Next is gated on that step's
  // required fields — so "already advanced past, or standing on it" is exactly
  // "cannot skip ahead past unfilled required fields" (per Ugur 2026-08-05).
  // Preview navigates freely; a reviewer is not filling the form in.
  const railLocked = useMemo(() => {
    if (mode === 'preview') return new Set<string>();
    return new Set(
      allSteps.filter((s) => !advancedSteps.has(s.id) && s.id !== activeStepId).map((s) => s.id),
    );
  }, [mode, allSteps, advancedSteps, activeStepId]);

  // Cross-field uniqueness lookup — passed down to every ShortText's on-blur
  // check via FormRenderer. Returns the peer field's advisor-facing label
  // (in the current language) when the value collides, so the error message
  // can name the culprit ("This email is already used by Beneficiary").
  // Shared visibility gate used by BOTH the on-blur uniqueness check
  // AND the workflow-wide `findAllUniquenessConflicts` walk. Extracting
  // it keeps the two views consistent — an inline error can't accuse a
  // peer that findAllUniquenessConflicts wouldn't count (which would
  // paint red while Next stays enabled). Reads step visibility from the
  // memoized map; section + field conditions from evaluateConditions.
  const isFieldVisibleForUniqueness = useCallback(
    (field: WorkflowField, stepId: string): boolean => {
      const step = allSteps.find((s) => s.id === stepId);
      if (!step) return false;
      if (!(stepVisibility.get(stepId) ?? true)) return false;
      const section = step.sections.find((sec) =>
        sec.rows.some((r) => r.columns.some((c) => c.field?.id === field.id)),
      );
      if (
        section &&
        (section.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(section.conditions, section.conditionCombinator, values)
      ) {
        return false;
      }
      if (
        (field.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(field.conditions, field.conditionCombinator, values)
      ) {
        return false;
      }
      return true;
    },
    [allSteps, stepVisibility, values],
  );
  const findUniquenessConflictMessage = useCallback(
    (field: WorkflowField, next: FieldValue, ownValueKey?: string): string | null => {
      // Pass `next` as the override so the walker sees what the advisor
      // is about to commit without allocating a full values-map spread —
      // repeatable-section fields live under qualified keys the caller
      // doesn't know here, and the old spread wrote to the bare id.
      // `ownValueKey` is the current instance's key so the walker skips
      // the right instance (without it, editing a 2nd+ instance flags the
      // field against its own value — the repeatable-section false positive).
      const hit = findUniquenessConflict(
        field,
        template,
        values,
        (peerField, stepId) => isFieldVisibleForUniqueness(peerField, stepId),
        next,
        ownValueKey,
      );
      if (hit === null) return null;
      const peer =
        (language === 'fr' && hit.peer.labelFr ? hit.peer.labelFr : hit.peer.labelEn) ||
        hit.peer.labelEn;
      // Name the STEP too. Peer labels repeat across a workflow (RDSP has six
      // "Mobile Phone" fields in one group), so the label alone produced
      // "already used by 'Mobile Phone'" while the advisor stood on a field
      // called Mobile Phone — self-accusing and unactionable. Fall back to the
      // bare message only if the step somehow has no title in either locale.
      const step =
        (language === 'fr' && hit.step.titleFr ? hit.step.titleFr : hit.step.titleEn) ||
        hit.step.titleEn;
      return step
        ? t('formRenderer.validation.uniquenessOnStep', { peer, step })
        : t('formRenderer.validation.uniqueness', { peer });
    },
    [values, template, language, isFieldVisibleForUniqueness, t],
  );
  // Active-step fallback: if the currently active step has been hidden by a
  // recent value change, jump forward to the first visible step. (If no step
  // is visible at all, we still render the first step so the form doesn't
  // blank out — the rail will show every step greyed.) The `?? null` on the
  // last fallback is for the zero-step workflow path; the render below
  // short-circuits in that case.
  // In preview mode a hidden active step (landed on via ?stepId=) is honored so
  // the reviewer can inspect it; advisor mode discards it and falls back to the
  // first visible step. The rail still omits hidden steps in preview, so there's
  // no way to navigate BACK to it via the rail — only via the deep link.
  const activeStep =
    allSteps.find((s) => s.id === activeStepId && (mode === 'preview' || isVisible(s))) ??
    visibleSteps[0] ??
    allSteps[0];

  // Prev/next-step targets — nearest VISIBLE step on either side. Memoized
  // because each computation walks `allSteps` and was happening on every
  // keystroke.
  const { prevStep, nextStep } = useMemo(() => {
    if (!activeStep) return { prevStep: null, nextStep: null };
    const activeIndex = allSteps.findIndex((s) => s.id === activeStep.id);
    const prev =
      activeIndex > 0
        ? ([...allSteps.slice(0, activeIndex)].reverse().find(isVisible) ?? null)
        : null;
    const next =
      activeIndex >= 0 ? (allSteps.slice(activeIndex + 1).find(isVisible) ?? null) : null;
    return { prevStep: prev, nextStep: next };
  }, [allSteps, activeStep, isVisible]);

  // Three gates on Next:
  //   1. Blocking-value: any visible field on the active step whose current
  //      value matches its originator-authored `blockingValues` gate (e.g.
  //      "Resident = no" pins Next forever). Wins over everything else —
  //      filling more fields can't unblock a dead-end answer.
  //   2. Missing required: visible required fields that are still empty.
  //   3. No target: no visible next step in document order.
  const blockingField = useMemo(
    () => (activeStep ? findBlockingField(activeStep, values) : null),
    [activeStep, values],
  );
  // Precompute uniqueness-group offenders once per (template, values,
  // stepVisibility). `findAllUniquenessConflicts` respects field/section
  // visibility so a hidden peer's value doesn't count against a visible
  // peer's — otherwise a hidden AH2 would block advance the moment AH1
  // + AH2 shared a placeholder value.
  const uniquenessOffenders = useMemo(
    () =>
      findAllUniquenessConflicts(template, values, (field, stepId) =>
        isFieldVisibleForUniqueness(field, stepId),
      ),
    [template, values, isFieldVisibleForUniqueness],
  );
  const missingFields = useMemo(
    () =>
      activeStep
        ? missingRequiredFields(
            activeStep,
            values,
            language,
            uniquenessOffenders,
            uploadedFiles,
            user,
          )
        : [],
    [activeStep, values, language, uniquenessOffenders, uploadedFiles, user],
  );
  // Per-step completion map — built once per (allSteps, values, advancedSteps,
  // stepVisibility) change. Replaces the per-StepItem `isStepComplete(step,
  // values)` call which used to walk the same tree N times per keystroke.
  const stepCompletion = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of allSteps) {
      const visible = stepVisibility.get(s.id) ?? true;
      const advanced = advancedSteps.has(s.id);
      map.set(
        s.id,
        visible && advanced && isStepComplete(s, values, uniquenessOffenders, uploadedFiles, user),
      );
    }
    return map;
  }, [allSteps, values, advancedSteps, stepVisibility, uniquenessOffenders, uploadedFiles, user]);

  if (allSteps.length === 0 || !activeStep) {
    return <div style={{ padding: 24, color: '#475569' }}>{t('formRenderer.noSteps')}</div>;
  }
  const nextDisabled =
    blockingField !== null || missingFields.length > 0 || !nextStep || stepBlocker !== null;
  // Shared by the nav row's Next button and the Welcome step's CONTINUE:
  // mark the step we're leaving as "advanced past" so the rail can paint its
  // blue check, then move to the nearest visible next step.
  const advanceToNext = () => {
    if (!nextStep) return;
    // "Next" is this stub's stand-in for a save (see the nav-row comment below) — counts
    // as session activity so the refresh timer resets rather than firing mid-step.
    notifySessionActivity();
    setAdvancedSteps((s) => {
      if (s.has(activeStep.id)) return s;
      const next = new Set(s);
      next.add(activeStep.id);
      return next;
    });
    setActiveStepId(nextStep.id);
  };
  // Rail step list — the full list MINUS Welcome, in both modes.
  //
  // 2026-08-05: preview used to drop hidden steps entirely (`mode === 'preview'
  // ? visibleSteps : allSteps`, per user 2026-07-15) so the previewer saw
  // exactly an applicant's flow. Reversed at Ugur's request: a step that does
  // not apply is now SHOWN but disabled, matching how the source smart forms
  // present their own left nav. Keeping the row in place means the reader can
  // see the whole shape of the application and understand what their answers
  // switched off, instead of watching entries vanish.
  //
  // Welcome is the one exception: the rail is hidden outright while it's the
  // active step (it's a full-bleed intro card with its own CANCEL / CONTINUE),
  // so a Welcome row could only ever be a one-way door back out of the flow's
  // navigation. It's dropped from the rail entirely.
  //
  // `visibleSteps` still drives navigation (Next/Back, active-step fallback),
  // so a disabled row is inert, not merely unstyled — and dropping Welcome
  // here does not remove it from the flow itself.
  // Plain filter, not a useMemo — this sits after the `!activeStep` early
  // return above, so a hook here would break hook order.
  const railSteps = allSteps.filter((s) => s.type !== 'Welcome');
  // Tooltip priority: step-level blocker (e.g. email verify) > dead-end
  // answer > unfilled-required > no-target > none. Step-level takes the top
  // slot because the reason is authored copy tailored to that step's
  // failure. Show up to 3 specific field labels for "unfilled"; collapse to
  // a generic message when there are more so the tooltip stays readable.
  let nextTooltip = '';
  if (nextDisabled) {
    if (stepBlocker) {
      nextTooltip = stepBlocker;
    } else if (blockingField) {
      // Prefer the originator-authored block-continue explanation (FR falls
      // back to EN); generic "doesn't qualify" copy only when none authored.
      const authored =
        (language === 'fr' && blockingField.blockContinueMessageFr?.trim()
          ? blockingField.blockContinueMessageFr
          : blockingField.blockContinueMessageEn) ?? '';
      if (authored.trim()) {
        nextTooltip = authored.trim();
      } else {
        const label =
          (language === 'fr' && blockingField.labelFr
            ? blockingField.labelFr
            : blockingField.labelEn) || blockingField.labelEn;
        nextTooltip = t('formRenderer.nextBlockedByAnswer', {
          field: label.replace(/\*\*/g, '').trim(),
        });
      }
    } else if (missingFields.length > 0) {
      nextTooltip = t(
        missingFields.length <= 3
          ? 'formRenderer.nextBlockedFields'
          : 'formRenderer.nextBlockedManyFields',
        { fields: missingFields.join(', ') },
      );
    } else {
      nextTooltip = t('formRenderer.nextBlockedNoTarget');
    }
  }

  return (
    // Single white surface wrapping both rail + form, with a grey vertical
    // divider between them — matches the BMO design reference where the step
    // rail and main form read as a unified card, not two floating panels.
    // `flex: 1` + `minHeight` makes the card stretch to fill the available
    // content area so the rail extends down to the footer instead of
    // floating as a short panel on tall viewports.
    // `.form-shell` is a CSS container (`container-type: inline-size`); all the
    // responsive reflow — rail → floating overlay, nav buttons stacking, main
    // padding — lives in `@container` rules in theme/global.css so it fires off
    // THIS element's own width wherever the shell is mounted (device-preview
    // frame or a genuinely narrow viewport), with no JS measurement. The only
    // JS state left is `railOpen`, surfaced as the `--rail-open` modifier.
    <div className={`form-shell${railOpen ? ' form-shell--rail-open' : ''}`}>
      {/* Rail (+ its mobile toggle/scrim) is hidden entirely on the Welcome
          step — that step has no notion of "other steps" to navigate to. */}
      {activeStep.type !== 'Welcome' && (
        <>
          {/* Floating rail toggle — hidden by CSS until the shell is narrow, then
              pinned top-left to open the rail overlay. */}
          <Tooltip title={t('formRenderer.showSteps')}>
            <Button
              className="form-shell__rail-toggle"
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setRailOpen((open) => !open)}
              aria-label={t('formRenderer.showSteps')}
            />
          </Tooltip>
          {/* Scrim — hidden by CSS unless narrow AND open; click to close. Its only
              job is "click outside to dismiss", which is a pointer affordance with
              no keyboard equivalent to duplicate: a keyboard user never reaches the
              overlay state's trap because the rail toggle itself is a real button
              and Escape closes it (below). Marked aria-hidden so it isn't announced
              as a stray interactive element. */}
          <div aria-hidden className="form-shell__rail-scrim" onClick={() => setRailOpen(false)} />
          {/* One rail: an in-flow column when wide, an absolute slide-in overlay
              when narrow — the switch is entirely CSS. */}
          <div className="form-shell__rail">
            <StepRail
              steps={railSteps}
              disabledStepIds={railDisabled}
              lockedStepIds={railLocked}
              activeStepId={activeStep.id}
              language={language}
              stepCompletion={stepCompletion}
              // 2026-08-05: the rail is clickable in BOTH modes now. It used to be
              // `mode === 'preview'`, so in the advisor runtime no entry could be
              // clicked at all. Per Ugur: steps are clickable by default, and a
              // step whose conditions are not met is disabled — StepRail refuses
              // those per row, so this only ever lands on an applicable step.
              onSelect={(id) => {
                setActiveStepId(id);
                // Harmless when wide (rail is static, overlay transform unused).
                setRailOpen(false);
              }}
              clickable
            />
          </div>
        </>
      )}
      <main className="form-shell__main">
        <div style={{ flex: 1, minWidth: 0 }}>
          <StepContent
            // Re-mount on step switch so internal field components (date
            // pickers, modals, upload trackers) cleanly reset their local
            // UI state across step navigation.
            key={activeStep.id}
            template={template}
            step={activeStep}
            values={values}
            onSetStepBlocker={setStepBlocker}
            onChange={(fieldId, next) => {
              // Mark the field as manually edited so cross-step prefill
              // stops overwriting it. Guarded so we only tick state when
              // we're actually FLIPPING an unedited slot to edited —
              // otherwise every keystroke would bump the tick.
              if (!editedFieldsRef.current.has(fieldId)) {
                editedFieldsRef.current.add(fieldId);
                setEditedTick((t) => t + 1);
              }
              setValues((prev) => ({ ...prev, [fieldId]: next }));
            }}
            onJumpToStep={(id) => setActiveStepId(id)}
            language={language}
            mode={mode}
            pdfCache={pdfCacheRef.current}
            findUniquenessConflictMessage={findUniquenessConflictMessage}
            mutateValues={(updater) => setValues((prev) => updater(prev))}
            uploadedFiles={uploadedFiles}
            onUploadedFilesChange={setUploadedFiles}
            pdfLanguage={pdfLanguage}
            onPdfLanguageChange={setPdfLanguage}
            onContinue={advanceToNext}
            onCancelExit={onSaveAndExit}
          />
        </div>
        {/* Nav row — Back on the left, Save and exit + Next on the right.
            Next "saves" by calling the same onSaveAndExit semantics later
            (stub for now since advisor persistence isn't wired). Disabled
            states at flow edges. When the shell is collapsed (mobile-width
            preview) the buttons stack full-width — Back → Save and exit →
            Submit — instead of crowding into one overflowing row.
            Hidden entirely on the Welcome step — its card owns its own
            CANCEL / CONTINUE actions. */}
        {activeStep.type !== 'Welcome' && (
          <div
            className="form-shell__nav"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid #ececee',
            }}
          >
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={!prevStep}
              onClick={() => prevStep && setActiveStepId(prevStep.id)}
            >
              {t('formRenderer.back')}
            </Button>
            <div className="form-shell__nav-actions" style={{ display: 'flex', gap: 8 }}>
              <Button
                icon={<SaveOutlined />}
                onClick={() => {
                  // BMO persistence integration is deferred — for now this
                  // button just navigates out via `onSaveAndExit`. When the
                  // BMO autosave wire ships, the trigger fires here too.
                  notifySessionActivity();
                  void message.success(t('formRenderer.draftSaved'));
                  onSaveAndExit?.();
                }}
              >
                {t('formRenderer.saveAndExit')}
              </Button>
              {activeStep.type === 'PdfGeneration' ? (
                // Final step swaps Next for a primary Download action — there's
                // nothing left to advance to. Runs the same fetch → pdf-lib
                // fill → download pipeline as the per-row buttons, once per
                // uploaded template. Missing files toast the stub message; any
                // fetch/fill error surfaces via the generic preview-failed copy.
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    // Stand-in for the eventual real Submit call (see
                    // docs/pretend-advisor.md) — counts as session activity.
                    notifySessionActivity();
                    void (async () => {
                      // Client's chosen PDF language (from the Welcome step
                      // radio, if authored — otherwise the mount-time
                      // initial) drives the S3 template pick, not the
                      // advisor's UI language.
                      const docs = collectPdfDocs(template, pdfLanguage, values);
                      const withFiles = docs.filter((d) => d.previewKey);
                      // Flatten uploaded documents so the zip bundle
                      // contains BOTH the pdf-lib-filled templates AND
                      // every file the advisor picked on any
                      // AdditionalDocuments step earlier in the flow.
                      // Each named entry contributes its filenameTemplate
                      // so a mandatory Transfer form / Void cheque gets
                      // renamed inside the zip; optional-bucket files
                      // keep their original names.
                      const entryMeta: Record<
                        string,
                        { label: string; filenameTemplate?: string | null }
                      > = {};
                      for (const s of template.steps) {
                        if (s.type !== 'AdditionalDocuments') continue;
                        for (const e of s.additionalDocumentEntries ?? []) {
                          entryMeta[e.id] = {
                            label: e.label,
                            filenameTemplate: e.filenameTemplate,
                          };
                        }
                      }
                      const uploaded = flattenUploadedFiles(
                        uploadedFiles,
                        entryMeta,
                        values,
                        template,
                      );
                      if (withFiles.length === 0 && uploaded.length === 0) {
                        void message.info(t('formRenderer.pdfGeneration.stubDownloadAll'));
                        return;
                      }
                      try {
                        // Everything goes into a single zip so the advisor
                        // gets one file, not N sequential downloads that
                        // browsers rate-limit and misorder. Predictable
                        // shape for the caller: Download all → one zip,
                        // no matter the count. Per-row Download button on
                        // PdfGenerationStep still delivers the templated
                        // single file directly when the advisor only
                        // wants one.
                        const zip = new JSZip();
                        // De-dupe collisions on filename (two mandatory
                        // rows named "form.pdf" or two bucket uploads
                        // sharing a name) — append " (2)" / " (3)" etc.
                        // before the extension.
                        const usedNames = new Set<string>();
                        const uniqueName = (raw: string): string => {
                          if (!usedNames.has(raw)) {
                            usedNames.add(raw);
                            return raw;
                          }
                          const dot = raw.lastIndexOf('.');
                          const stem = dot >= 0 ? raw.slice(0, dot) : raw;
                          const ext = dot >= 0 ? raw.slice(dot) : '';
                          let i = 2;
                          while (usedNames.has(`${stem} (${i})${ext}`)) i += 1;
                          const candidate = `${stem} (${i})${ext}`;
                          usedNames.add(candidate);
                          return candidate;
                        };
                        for (const d of withFiles) {
                          // Reuse the PDF-lib cache when possible — the
                          // PdfGeneration step normally pre-generates by
                          // the time the user hits Download all. Cache
                          // miss or stale falls through to a fresh fill;
                          // the result is written back so the per-row
                          // buttons see it too.
                          const existing = pdfCacheRef.current.get(d.entryId);
                          let blobUrl: string;
                          if (existing && existing.values === values) {
                            blobUrl = existing.url;
                          } else {
                            blobUrl = await buildFilledPdfBlobUrl(
                              template.id,
                              template,
                              d.previewKey!,
                              values,
                              d.entryId,
                            );
                            if (existing) URL.revokeObjectURL(existing.url);
                            pdfCacheRef.current.set(d.entryId, { values, url: blobUrl });
                          }
                          const bytes = await (await fetch(blobUrl)).arrayBuffer();
                          zip.file(uniqueName(d.fileName), bytes);
                        }
                        for (const u of uploaded) {
                          zip.file(uniqueName(u.fileName), u.file);
                        }
                        const zipBlob = await zip.generateAsync({
                          type: 'blob',
                          compression: 'DEFLATE',
                          compressionOptions: { level: 6 },
                        });
                        const zipUrl = URL.createObjectURL(zipBlob);
                        // Zip name follows the same shape as the individual
                        // PDFs — take the first PDF entry's rendered stem
                        // and append `.zip` so the archive lands as
                        // e.g. `RDSP1_Smith_07072026.zip` instead of the
                        // generic `<appName>-documents.zip`. Falls back to
                        // the first uploaded file's stem OR the appName
                        // when no PDFs are present.
                        const firstNamed =
                          withFiles[0]?.fileName ??
                          uploaded[0]?.fileName ??
                          `${template.appName || 'workflow'}-documents.pdf`;
                        const stem = firstNamed.replace(/\.[^.]+$/, '');
                        const zipName = `${stem}.zip`;
                        triggerBlobDownload(zipUrl, zipName);
                        // Give the browser time to hand the download to
                        // the OS before we revoke.
                        setTimeout(() => URL.revokeObjectURL(zipUrl), 5_000);
                      } catch {
                        void message.error(t('formRenderer.pdfGeneration.previewFailed'));
                      }
                    })();
                  }}
                >
                  {t('formRenderer.pdfGeneration.downloadAll')}
                </Button>
              ) : (
                <Tooltip title={nextDisabled ? nextTooltip : ''}>
                  {/* AntD disables tooltip on a disabled button by default —
                    wrap in a span so the tooltip still fires on hover over
                    the disabled state. */}
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      type="primary"
                      icon={<ArrowRightOutlined />}
                      disabled={nextDisabled}
                      onClick={advanceToNext}
                      style={nextDisabled ? { pointerEvents: 'none' } : undefined}
                    >
                      {t('formRenderer.next')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Renders the active step's body. Dispatches by `step.type` — DynamicForm
 * routes to the form renderer; AdditionalDocuments to the file-upload UI;
 * every other type renders a placeholder card. Centralizes the dispatch so
 * FormShell stays compact.
 */
function StepContent({
  template,
  step,
  values,
  onChange,
  onJumpToStep,
  onSetStepBlocker,
  language,
  mode,
  pdfCache,
  findUniquenessConflictMessage,
  mutateValues,
  uploadedFiles,
  onUploadedFilesChange,
  pdfLanguage,
  onPdfLanguageChange,
  onContinue,
  onCancelExit,
}: {
  template: WorkflowTemplate;
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  onChange: (fieldId: string, next: FieldValue) => void;
  /** ApplicationReview row's edit pencil jumps back to the originating step. */
  onJumpToStep: (stepId: string) => void;
  /** Step-level Next blocker. Currently only OneSpanESignStep uses this
   *  (INFLOWS-188 email verification). Pass null when the step is ready to
   *  advance; a non-null string is used as the disabled-button tooltip. */
  onSetStepBlocker: (reason: string | null) => void;
  language: 'en' | 'fr';
  mode: 'preview' | 'advisor';
  /** FormShell-owned pdf-lib fill cache — passed through so PdfGenerationStep
   *  can seed its ready map on remount without spinning back through the
   *  fetch → fill pipeline for values that haven't changed. */
  pdfCache: PdfCache;
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  mutateValues?: (
    updater: (prev: Record<string, FieldValue>) => Record<string, FieldValue>,
  ) => void;
  /** Shared advisor-uploaded document store — see FormShell for shape. */
  uploadedFiles: UploadedFilesStore;
  onUploadedFilesChange: (
    updater: UploadedFilesStore | ((prev: UploadedFilesStore) => UploadedFilesStore),
  ) => void;
  /** Client's chosen PDF language — separate from the advisor's UI locale.
   *  Set by the Welcome step's radio when the workflow has one; drives ONLY
   *  which S3 template gets pulled + filled at PdfGeneration + e-sign. */
  pdfLanguage: 'en' | 'fr';
  /** Welcome step's language radio — updates FormShell's `pdfLanguage`
   *  state (does NOT touch the advisor's UI locale). */
  onPdfLanguageChange: (pdfLanguage: 'en' | 'fr') => void;
  /** Welcome step's CONTINUE — same semantics as the nav row's Next. */
  onContinue: () => void;
  /** Welcome step's CANCEL — exits the flow (same as Save and exit). */
  onCancelExit?: () => void;
}) {
  if (step.type === 'Welcome') {
    // `language` is the advisor's UI locale (from the AppShell header
    // switcher) — drives the Welcome card's own copy (title / description /
    // bullets). `pdfLanguage` is the client's chosen PDF language — the
    // radio binds to it and the callback ONLY updates that, so picking
    // French no longer flips the whole shell.
    return (
      <WelcomeStep
        template={template}
        step={step}
        language={language}
        pdfLanguage={pdfLanguage}
        onPdfLanguageChange={onPdfLanguageChange}
        onContinue={onContinue}
        onCancel={onCancelExit}
      />
    );
  }
  if (step.type === 'DynamicForm') {
    return (
      <FormRenderer
        step={step}
        values={values}
        onChange={onChange}
        language={language}
        mode={mode}
        findUniquenessConflictMessage={findUniquenessConflictMessage}
        mutateValues={mutateValues}
      />
    );
  }
  if (step.type === 'ApplicationReview') {
    // Threads the advisor's uploaded-doc store so the review page recaps
    // "Uploaded documents" + "Generated forms" alongside the entered-data
    // collapse (INFLOWS-201 AC2).
    return (
      <ApplicationReviewStep
        template={template}
        step={step}
        values={values}
        language={language}
        uploadedFiles={uploadedFiles}
        onJumpToStep={onJumpToStep}
      />
    );
  }
  if (step.type === 'AdditionalDocuments') {
    return (
      <AdditionalDocumentsStep
        step={step}
        language={language}
        values={values}
        uploadedFiles={uploadedFiles}
        onUploadedFilesChange={onUploadedFilesChange}
      />
    );
  }
  if (step.type === 'PdfGeneration') {
    // Lists every populated PDF that would be generated for the advisor,
    // plus every advisor-uploaded document threaded through from any
    // AdditionalDocuments step earlier in the flow. No step-blocker
    // wiring here: FormShell hard-swaps Next for a Download button on
    // this step type (see the nav row below), so any blocker published
    // from inside PdfGenerationStep would be inert. If a future flow
    // ever places a step AFTER PdfGeneration with a real Next, revisit
    // this — a generation-in-flight gate would want to live at that
    // downstream Next, not on the PdfGeneration step itself.
    return (
      <PdfGenerationStep
        template={template}
        // PDF template pick uses the client's chosen language (from the
        // Welcome step's radio), NOT the advisor's UI locale.
        language={pdfLanguage}
        values={values}
        cache={pdfCache}
        uploadedFiles={uploadedFiles}
      />
    );
  }
  if (step.type === 'OneSpanESign') {
    // Resolved SIGNATORY payload preview — signers + per-PDF placements.
    // Runtime mode (advisor) also fires ZeroBounce email verification and
    // blocks Next on Invalid signatory emails (INFLOWS-188).
    return (
      <OneSpanESignStep
        template={template}
        step={step}
        values={values}
        language={language}
        mode={mode}
        onSetStepBlocker={onSetStepBlocker}
        onChange={onChange}
      />
    );
  }
  // PdfFileUpload (filtered from advisor rail upstream) — placeholder card.
  return <PlaceholderStep step={step} language={language} />;
}

function StepRail({
  steps,
  activeStepId,
  language,
  stepCompletion,
  onSelect,
  clickable,
  disabledStepIds,
  lockedStepIds,
}: {
  steps: WorkflowStep[];
  activeStepId: string;
  /** Steps whose conditions are not met. Rendered in place but inert, so the
   *  reader can see what their answers switched off. */
  disabledStepIds: Set<string>;
  /** Advisor only: steps not yet reached. Inert, but NOT greyed — "not there
   *  yet" must stay visually distinct from "does not apply to you". Empty in
   *  preview mode. */
  lockedStepIds: Set<string>;
  language: 'en' | 'fr';
  /** Per-step "should the blue check show" map — pre-computed by FormShell
   *  with the visible / advanced / isStepComplete gates already applied so
   *  this component doesn't re-walk the field tree on every keystroke. */
  stepCompletion: Map<string, boolean>;
  onSelect: (stepId: string) => void;
  clickable: boolean;
}) {
  return (
    <nav
      style={{
        width: 280,
        flexShrink: 0,
        // Plain white surface + 1px right divider. Keeps the rail quiet so
        // the active-step highlight + check icons are the only visual cues.
        // Top padding matches the main pane so the first rail item lines
        // up with the section heading on the canvas — tightened from 32
        // to 20 so the form opens with less wasted vertical real estate.
        background: '#fff',
        borderRight: '1px solid #e5e7eb',
        padding: '20px 0 16px 0',
        position: 'relative',
      }}
    >
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {steps.map((step) => (
          <StepItem
            key={step.id}
            step={step}
            isActive={step.id === activeStepId}
            isComplete={stepCompletion.get(step.id) ?? false}
            language={language}
            clickable={clickable && !disabledStepIds.has(step.id) && !lockedStepIds.has(step.id)}
            isDisabled={disabledStepIds.has(step.id)}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </nav>
  );
}

/**
 * A single rail entry. Four visual states, compounded from isComplete +
 * isActive:
 *
 *   - Completed + not active: BMO-blue check icon + blue text.
 *   - Completed + active: BMO-blue check icon + BLACK bold text (the originator
 *     opened a completed step to inspect it).
 *   - Not completed + not active: grey text, NO icon (icon column reserves
 *     width but is empty so titles still align).
 *   - Not completed + active: grey-not-shown icon + black bold text.
 *
 * Check icon visibility is driven purely by isComplete — never appears on a
 * not-yet-filled step. The icon color is therefore always BMO blue when shown.
 *
 * Wrapped in `React.memo` so a keystroke on the active step's form doesn't
 * re-render every rail row — each StepItem's inputs are scalar primitives +
 * a stable onSelect handler, so the default shallow compare is sufficient.
 */
const StepItem = memo(function StepItem({
  step,
  isDisabled = false,
  isActive,
  isComplete,
  language,
  clickable,
  onSelect,
}: {
  step: WorkflowStep;
  isActive: boolean;
  isComplete: boolean;
  /** Conditions not met — shown for context but inert. */
  isDisabled?: boolean;
  language: 'en' | 'fr';
  clickable: boolean;
  onSelect: (stepId: string) => void;
}) {
  const title = language === 'fr' && step.titleFr ? step.titleFr : step.titleEn;
  // Preview mode: the caller (FormShell) already filters hidden steps out of
  // the rail's step list, so a step reaching this row is currently visible.
  // Advisor mode still passes the full list; a hidden step renders normally
  // there but never earns the completed-check (the caller computes
  // `isComplete` with the visibility gate baked in).
  // Per the client's 2026-07-24 Figma the rail is typographic only — state is
  // carried by weight + colour + the check icon, with no background tint and
  // no accent bar:
  //   • complete          → BMO blue + filled check icon in the left cell
  //   • active            → near-black, bold, no icon
  //   • complete + active → check icon stays, but active wins the colour so
  //                         the reader can still find where they are
  //   • neither           → muted grey
  // Disabled outranks complete/muted. #9ca3af is a step lighter than the
  // #475569 "not yet complete" grey, so the two states stay distinguishable —
  // muted grey means "not done", this lighter grey + 60% opacity means "does
  // not apply to your answers".
  //
  // #9ca3af is 2.54:1 on white and would fail 1.4.3, but WCAG explicitly
  // exempts "text or images of text that are part of an inactive user
  // interface component". These steps are inactive by definition — the rail
  // marks them disabled and refuses navigation — and the low contrast IS the
  // signal that they don't apply. Kept deliberately; do not sweep it with the
  // other greys. The steps are also conveyed as disabled programmatically
  // (aria-disabled), so the meaning does not rest on colour alone (1.4.1).
  const textColor = isDisabled
    ? '#9ca3af'
    : isActive
      ? '#111827'
      : isComplete
        ? '#0179C1'
        : '#475569';
  return (
    <li style={{ listStyle: 'none', position: 'relative' }}>
      <button
        type="button"
        onClick={() => onSelect(step.id)}
        disabled={!clickable || isDisabled}
        aria-disabled={isDisabled || undefined}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          // 15px left inset preserves the title alignment the old 3px accent
          // bar + 12px padding produced, so removing the bar doesn't shift
          // every row sideways.
          padding: '8px 16px 8px 15px',
          cursor: isDisabled ? 'not-allowed' : clickable ? 'pointer' : 'default',
          opacity: isDisabled ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          position: 'relative',
          zIndex: 1,
          transition: 'color 140ms ease',
        }}
      >
        {/* Icon cell — fully transparent so non-complete rows don't read as
            empty checkboxes. The check icon (only present when complete) is
            the lone visual marker; the cell reserves width so step titles
            still align across rows. */}
        <span
          style={{
            width: 18,
            height: 18,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isComplete && <CheckCircleFilled style={{ color: '#0179C1', fontSize: 16 }} />}
        </span>
        <Text
          strong={isActive}
          style={{
            fontSize: 14,
            color: textColor,
            letterSpacing: 0.1,
          }}
        >
          {title}
        </Text>
      </button>
    </li>
  );
});

/**
 * "Completed" = every required field on the step has a filled value. Layout
 * blocks (StaticText / Paragraph / Divider) are skipped — they're non-input.
 * A step with zero required fields is vacuously complete from the start.
 *
 * Non-DynamicForm step types (PdfFileUpload / AdditionalDocuments /
 * OneSpanESign / PdfGeneration) report false until their own runtime
 * completion semantics land — otherwise an empty-sections step would always
 * read as "done" the moment the advisor opens it.
 */
function isStepComplete(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
  uniquenessOffenders: Set<string>,
  uploadedFiles: UploadedFilesStore,
  /** See missingRequiredFields — same exemption rule, same optionality. */
  sessionUser?: MeResponse | null,
): boolean {
  const isRequiredExempt = requiredExemptionFor(sessionUser);
  // Welcome has no inputs — it's complete by construction; the rail check
  // still waits for `advancedSteps` (i.e. CONTINUE pressed) upstream.
  if (step.type === 'Welcome') return true;
  // AdditionalDocuments is complete once every REQUIRED entry has at least
  // one file in the store. The optional bucket doesn't gate completion —
  // it's optional by definition. INFLOWS-201 AC1: "documents ... complete"
  // is enforced here so a required-doc entry left empty blocks Next (via
  // `missingRequiredFields` below) and dims the rail check for this step.
  if (step.type === 'AdditionalDocuments') {
    for (const entry of step.additionalDocumentEntries ?? []) {
      if (!entry.isRequired) continue;
      // Entry-level visibility conditions — mirror the DynamicForm section
      // pattern below. An entry hidden by unmet conditions doesn't count
      // toward completion (otherwise Next would stall on a "required" doc
      // the advisor can't even see).
      if (
        (entry.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(entry.conditions, entry.conditionCombinator, values)
      ) {
        continue;
      }
      if ((uploadedFiles.entries[entry.id] ?? []).length === 0) return false;
    }
    return true;
  }
  if (step.type !== 'DynamicForm') return false;
  const getValue = (id: string): FieldValue => values[id];
  const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);
  for (const section of step.sections) {
    // A section gated by visibility conditions: only skip it when the rule
    // currently HIDES the section. If the rule matches and the section is
    // showing, its required fields still count toward step completion —
    // otherwise the rail's blue check fires while visible-required-empty
    // fields wait for input.
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    // §1 Repeatable Sections: every instance's values live under
    // `inst:<sectionId>:<idx>:<fieldId>` keys. Walk the section field-by-
    // field and, for each authored field, check EVERY instance's slot —
    // otherwise a repeatable section's required fields read as empty and
    // Next never enables. Also handles the RepeatableRow-inside-repeatable
    // corner: the row-array itself lives at a qualified key.
    const instanceCount = section.repeatable
      ? getInstanceCount(values, section.id, sectionMinInstances(section))
      : 1;
    for (const row of section.rows) {
      for (const col of row.columns) {
        const f = col.field;
        if (!f) continue;
        if (isLayoutBlockType(f.type)) continue;
        // Borrowed by a CheckboxGroup option that isn't revealing it — the
        // field renders nowhere, so it can't gate completion. Mirrors the
        // same skip in missingRequiredFields.
        if (hiddenBorrowed.has(f.id)) continue;
        for (let i = 0; i < instanceCount; i++) {
          const key = section.repeatable ? `inst:${section.id}:${i}:${f.id}` : f.id;
          // Field-level conditions: same logic — only skip when the field is
          // currently hidden. Evaluated PER INSTANCE, because a reveal
          // inside a repeatable section is answered separately by each
          // instance: beneficiary 1 may have opened the custodial-parent
          // name fields while beneficiary 2 left them closed, and only
          // the open ones can hold back Next.
          if (
            (f.conditions?.length ?? 0) > 0 &&
            !evaluateConditions(
              f.conditions,
              f.conditionCombinator,
              scopeValuesToInstance(section, values, i),
            )
          ) {
            continue;
          }
          if (f.required && !isRequiredExempt(f) && !isFilled(values[key])) return false;
          // Cardinality floor on a CheckboxGroup — "at least N ticked", which
          // `required` (non-empty) can't express.
          if (!isRequiredExempt(f) && !meetsMinSelections(f.minSelections, values[key])) {
            return false;
          }
          if (
            f.type === 'Date' &&
            validateMinAge(
              f.minAge,
              typeof values[key] === 'string' ? (values[key] as string) : null,
            )
          ) {
            return false;
          }
          if (f.type === 'RepeatableRow') {
            const rows = (values[key] as RepeatableRowValue[] | undefined) ?? [];
            if (f.sumValidation && !isSumSatisfied(f, rows, getValue)) return false;
            if (incompleteRepeatableColumns(f, rows, 'en').length > 0) return false;
          }
        }
        // Uniqueness offender set is precomputed across the whole workflow
        // (visibility-aware) — a duplicate ANYWHERE blocks the offending
        // step's completion. Keyed on the bare authored field id today;
        // when uniqueness gains per-instance semantics the offender set
        // will hold qualified keys and this check will follow.
        if (f.uniquenessGroup && uniquenessOffenders.has(f.id)) return false;
      }
    }
  }
  return true;
}

/**
 * Walk the active step's visible fields and return the first one whose
 * current value matches its `blockingValues` gate (see isBlockingValueMatch).
 * That's the "dead-end answer" gate the originator authored — an explicit
 * eligibility short-circuit that disables Next regardless of how many other
 * fields are filled. Returns null when nothing's blocking.
 */
function findBlockingField(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): WorkflowField | null {
  // Only DynamicForm steps own input fields with blocking values.
  if (step.type !== 'DynamicForm') return null;
  for (const section of step.sections) {
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    for (const row of section.rows) {
      for (const col of row.columns) {
        const f = col.field;
        if (!f || !f.blockingValues || f.blockingValues.length === 0) continue;
        if (
          (f.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(f.conditions, f.conditionCombinator, values)
        ) {
          continue;
        }
        if (isBlockingValueMatch(f, values[f.id])) return f;
      }
    }
  }
  return null;
}

/**
 * Walk the active step's visible sections + fields and return the bilingual
 * labels of every required-but-empty field. Same visibility rules as
 * `isStepComplete` — a section / field hidden by current values doesn't
 * count. Returns an array (possibly empty) so the caller can decide whether
 * to surface "fill X, Y, Z" or a generic "fill all required fields" message.
 */
/**
 * Required COLUMNS inside a RepeatableRow that the advisor has left empty.
 *
 * The completeness walk only ever checked the field-level `required` (is the
 * row array non-empty) and `sumValidation` — a column marked required inside
 * the row template was never enforced, so a half-filled owner or trustee row
 * sailed past Next.
 *
 * Only rows the advisor has STARTED are checked: an untouched RepeatableRow
 * renders one blank placeholder row, and demanding its columns would block an
 * optional list nobody intended to fill. A field that must have at least one
 * entry expresses that with its own `required` flag, which is checked
 * separately.
 */
function incompleteRepeatableColumns(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  language: 'en' | 'fr',
): string[] {
  const template = field.repeatableTemplate ?? [];
  if (template.length === 0) return [];
  const out: string[] = [];
  for (const row of rows) {
    const started = template.some((c) => isFilled(row[c.id] as FieldValue));
    if (!started) continue;
    for (const col of template) {
      if (!col.required) continue;
      if (isFilled(row[col.id] as FieldValue)) continue;
      const label = (language === 'fr' && col.labelFr ? col.labelFr : col.labelEn) || col.labelEn;
      out.push(label);
    }
  }
  return out;
}

/**
 * True when a field's cross-step prefill source sits in the SAME uniqueness
 * group as the field itself — i.e. the authoring says "copy this value" and
 * "these two values must differ" at once.
 *
 * Exported for the test; see the call site in the prefill effect for why the
 * prefill is the side that yields.
 */
export function sharesUniquenessGroupWithSource(
  field: WorkflowField,
  fieldsById: Map<string, WorkflowField>,
): boolean {
  const group = field.uniquenessGroup?.trim();
  if (!group || !field.sourceField) return false;
  const source = fieldsById.get(field.sourceField.fieldId);
  return source?.uniquenessGroup?.trim() === group;
}

export function missingRequiredFields(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
  language: 'en' | 'fr',
  uniquenessOffenders: Set<string>,
  uploadedFiles: UploadedFilesStore,
  /** Signed-in advisor, for the read-only required-exemption rule. Optional so
   *  existing callers (and tests) that don't care keep working — a null session
   *  exempts every read-only field, which is the pre-AC3 behaviour. */
  sessionUser?: MeResponse | null,
): string[] {
  const isRequiredExempt = requiredExemptionFor(sessionUser);
  // AdditionalDocuments: report every required entry that has no uploaded
  // file, using the entry's label as the tooltip token. Optional bucket is
  // ignored (optional by definition).
  if (step.type === 'AdditionalDocuments') {
    const out: string[] = [];
    for (const entry of step.additionalDocumentEntries ?? []) {
      if (!entry.isRequired) continue;
      // See the matching branch in `isStepComplete` — conditional entries
      // that are currently hidden don't participate in the missing-fields
      // count.
      if (
        (entry.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(entry.conditions, entry.conditionCombinator, values)
      ) {
        continue;
      }
      if ((uploadedFiles.entries[entry.id] ?? []).length > 0) continue;
      out.push(entry.label);
    }
    return out;
  }
  // Only DynamicForm steps have field-level required-input semantics.
  if (step.type !== 'DynamicForm') return [];
  const out: string[] = [];
  const getValue = (id: string): FieldValue => values[id];
  const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);
  const seen = new Set<string>();
  const pushOnce = (msg: string): void => {
    if (seen.has(msg)) return;
    seen.add(msg);
    out.push(msg);
  };
  for (const section of step.sections) {
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    // §1 Repeatable Sections: walk every instance's qualified key when
    // the section is repeatable. Otherwise fall back to the bare field
    // id (one iteration). Deduping via `seen` keeps the tooltip terse
    // even when the same field is empty across multiple instances.
    const instanceCount = section.repeatable
      ? getInstanceCount(values, section.id, sectionMinInstances(section))
      : 1;
    const instanceLabel = (n: number): string | null =>
      section.repeatable ? ` (${renderInstanceLabel(section, n, language)})` : null;
    for (const row of section.rows) {
      for (const col of row.columns) {
        const f = col.field;
        if (!f) continue;
        if (isLayoutBlockType(f.type)) continue;
        // Borrowed by an option that isn't revealing it — invisible, so not
        // required. See hiddenBorrowedFieldIds.
        if (hiddenBorrowed.has(f.id)) continue;
        // Non-repeatable fast path. Inside a repeatable section the same
        // check runs per instance below, because each instance answers
        // the reveal for itself.
        if (
          !section.repeatable &&
          (f.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(f.conditions, f.conditionCombinator, values)
        ) {
          continue;
        }
        const label = (language === 'fr' && f.labelFr ? f.labelFr : f.labelEn) || f.labelEn;
        const cleaned = label.replace(/\*\*/g, '').trim();
        // A Date whose typed value breaks its min-age / no-future rule blocks
        // Next the same way an empty required field does. The picker only
        // greys the calendar; typed input reaches here.
        if (
          f.type === 'Date' &&
          validateMinAge(
            f.minAge,
            typeof values[f.id] === 'string' ? (values[f.id] as string) : null,
          )
        ) {
          pushOnce(cleaned);
        }
        let requiredHit = false;
        for (let i = 0; i < instanceCount; i++) {
          const key = section.repeatable ? `inst:${section.id}:${i}:${f.id}` : f.id;
          if (
            section.repeatable &&
            (f.conditions?.length ?? 0) > 0 &&
            !evaluateConditions(
              f.conditions,
              f.conditionCombinator,
              scopeValuesToInstance(section, values, i),
            )
          ) {
            continue;
          }
          if (f.required && !isRequiredExempt(f) && !isFilled(values[key])) {
            // Repeat-section: name the instance so the advisor knows
            // which one is short a required field.
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned);
            requiredHit = true;
          }
          // Enough boxes ticked? Named the same way as a missing required
          // field so the disabled-Next tooltip reads consistently.
          if (!isRequiredExempt(f) && !meetsMinSelections(f.minSelections, values[key])) {
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned);
            requiredHit = true;
          }
          if (f.type === 'RepeatableRow') {
            const rows = (values[key] as RepeatableRowValue[] | undefined) ?? [];
            // A dual-unit column whose ACTIVE mode carries a total must
            // balance too. Named per COLUMN, like the incomplete-column check
            // below, so the advisor knows WHICH total is off rather than just
            // that the table is wrong.
            for (const u of unbalancedUnitColumns(f, rows, getValue)) {
              const colLabel =
                (language === 'fr' ? u.column.labelFr : u.column.labelEn) ||
                u.column.labelEn ||
                u.column.labelFr;
              pushOnce(`${cleaned} — ${colLabel}`);
            }
            if (f.sumValidation && !isSumSatisfied(f, rows, getValue)) {
              const suffix = instanceLabel(i);
              pushOnce(suffix ? `${cleaned}${suffix}` : cleaned);
            }
            // Name the COLUMN, prefixed by the list, so "Owners of interest —
            // Category" tells the advisor exactly what to finish.
            for (const colLabel of incompleteRepeatableColumns(f, rows, language)) {
              pushOnce(`${cleaned} — ${colLabel}`);
            }
          }
        }
        if (requiredHit) continue;
        // Cross-field uniqueness — surface offenders on this step so the
        // tooltip names the affected input. The per-field inline error
        // still shows via the on-blur check.
        if (f.uniquenessGroup && uniquenessOffenders.has(f.id)) pushOnce(cleaned);
      }
    }
  }
  return out;
}

function isFilled(v: FieldValue): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return Number.isFinite(v);
  // Required Toggle / Checkbox is the acknowledgment pattern — must be ON
  // to count as "filled."
  if (typeof v === 'boolean') return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return false;
    // RepeatableRow value — array of objects keyed by column id. An array
    // of all-empty rows is functionally unfilled (the renderer creates a
    // placeholder row by default, so an untouched RepeatableRow looks like
    // `[{col_a: null, col_b: null}]`). Probe the FIRST non-null element to
    // distinguish RepeatableRow (objects) from CheckboxGroup (strings) —
    // a `[null, ...]` leading-null shape used to slip through the
    // `typeof v[0] === 'object'` check and unconditionally read as filled.
    const probe = (v as unknown[]).find((x) => x !== null && x !== undefined);
    if (typeof probe === 'object' && probe !== null) {
      return (v as Array<Record<string, unknown> | null>).some(
        (row) =>
          row !== null &&
          Object.values(row).some(
            (cell) =>
              cell !== null &&
              cell !== undefined &&
              cell !== '' &&
              // NaN slips through the !== '' check; treat as unfilled so a
              // corrupted numeric cell doesn't paint the rail's blue check.
              (typeof cell !== 'number' || Number.isFinite(cell)),
          ),
      );
    }
    // CheckboxGroup `string[]` — non-empty array is filled.
    return true;
  }
  return false;
}
